'use client';

import { createContext, type PropsWithChildren, useContext, useEffect, useMemo, useState } from 'react';
import Session from 'supertokens-auth-react/recipe/session';
import { UserRoleClaim } from 'supertokens-web-js/recipe/userroles';

import { type User, UserRole } from '@common/clients/api';
import { type UserContext } from '@common/defaults/UserContext';
import { fetchCurrentUserProfile } from '@common/handlers/fetchCurrentUserProfile';
import { useContextData } from '@common/useContextData';
import { isBettingDisabledForUser } from '@common/utils/betting/checkIfBettingEnabled';
import { setBettingCookie } from '@common/utils/betting/setBettingCookie';

import { getUserSubscriptionStatus, UserSubscriptionStatus } from './UserContextProvider.utils';

interface LoginState {
    isLoggedIn: boolean;
    isUnverified: boolean;
    userRole?: string;
}

type UserContextValue = UserContext & {
    state: LoginState;
    updateUser: () => Promise<User | undefined>;
    clearUser: () => void;
    isDisabled: boolean;
    subscriptionStatus: UserSubscriptionStatus;
};

const UserContext = createContext<UserContextValue>({
    user: undefined,
    userAgent: undefined,
    supertokens: undefined,
    state: {
        isLoggedIn: false,
        isUnverified: false,
    },
    updateUser: () => Promise.resolve(undefined),
    clearUser: () => {},
    isDisabled: false,
    subscriptionStatus: UserSubscriptionStatus.NOT_SUBSCRIBED,
});

interface LoggedInStatus {
    isLoggedIn: boolean;
    isUnverified: boolean;
    userRole?: UserRole;
}

function isUserRole(
    userRole: UserRole,
): userRole is 'user' | 'editor' | 'moderator' | 'admin' | 'user-banned' {
    return (
        userRole === UserRole.USER ||
        userRole === UserRole.EDITOR ||
        userRole === UserRole.MODERATOR ||
        userRole === UserRole.ADMIN ||
        userRole === UserRole.USER_BANNED
    );
}

export const useLoggedInStatus = (isDisabled: boolean): LoggedInStatus => {
    if (isDisabled) {
        return {
            isLoggedIn: false,
            isUnverified: false,
            userRole: undefined,
        };
    }

    // DO NOT allow Session.useClaimValue to be called when auth `isDisabled`
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const claimValue = Session.useClaimValue(UserRoleClaim);
    const userRole = claimValue.loading ? undefined : (claimValue.value?.[0] as UserRole | undefined);
    const isUnverified = userRole === UserRole.USER_NOT_VALIDATED;
    const isLoggedIn = userRole ? isUserRole(userRole) : false;

    return {
        isLoggedIn,
        isUnverified,
        userRole: userRole,
    };
};

type UserContextProviderProps = PropsWithChildren<{
    userContext: UserContext;
    isDisabled?: boolean;
}>;

export function UserContextProvider({
    children,
    userContext: initialUserContext,
    isDisabled = false,
}: UserContextProviderProps) {
    const contextData = useContextData();
    const [userContext, setUserContext] = useState<UserContext>(initialUserContext);
    const { supertokens, user } = userContext;
    const { isLoggedIn, userRole, isUnverified } = useLoggedInStatus(isDisabled);

    const updateUser = () =>
        fetchCurrentUserProfile(contextData, userContext).then((user) => {
            setUserContext((prev) => ({ ...prev, user }));

            // Always push betting cookie to false to oneLine if it should be disabled
            // TODO: Add a check on the current setting
            if (user && isBettingDisabledForUser(user)) {
                setBettingCookie(false);
            }
            return user;
        });
    const clearUser = () => setUserContext(initialUserContext);

    useEffect(() => {
        if (!supertokens || isDisabled) {
            return;
        }

        const { isUnverified: supertokensIsUnverified } = supertokens;
        if (supertokensIsUnverified !== isUnverified) {
            setUserContext((prev) => ({ ...prev, supertokens: { ...supertokens, isUnverified } }));
        }
    }, [isUnverified]);

    useEffect(() => {
        const isInvalidUser = !user || user.id !== userContext.user?.id;

        if ((isLoggedIn || isUnverified) && isInvalidUser) {
            updateUser();
        }

        if (!isLoggedIn || !isInvalidUser) {
            if (document) {
                document.body.classList.add('fully-hydrated');
            }
        }
    }, [isUnverified, isLoggedIn, userContext.user?.id]);

    const { allowAds, allowPremium, premiumSubscriptionTag } = contextData;
    const subscriptionStatus = getUserSubscriptionStatus({
        isLoggedIn: isLoggedIn,
        allowAds,
        allowPremium,
        premiumSubscriptionTag,
    });

    const value: UserContextValue = useMemo(
        () => ({
            ...userContext,
            state: {
                isLoggedIn,
                userRole,
                isUnverified,
            },
            updateUser,
            clearUser,
            isDisabled,
            subscriptionStatus,
        }),
        [userContext, isLoggedIn, userRole, isUnverified, subscriptionStatus],
    );

    return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

export function useUserContext() {
    const context = useContext(UserContext);

    if (!context) {
        throw new Error('useUserContext must be used within a UserProvider');
    }

    return context;
}
